"use client";

import { useMemo, useCallback } from "react";
import { useAuth } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import { normalizeOptionalWaveId } from "@/helpers/waves/wave.helpers";
import { mapApiWaveToSidebarWave, useWavesV2 } from "./useWavesV2";
import { SIDEBAR_WAVES_OVERVIEW_REFETCH_INTERVAL_MS, WAVE_FOLLOWING_WAVES_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import { usePinnedWavesServer } from "./usePinnedWavesServer";
import { useWaveById } from "./useWaveById";
import { useShowFollowingWaves } from "./useShowFollowingWaves";
import { useOfficialWaves } from "./useOfficialWaves";
import type { SidebarWave } from "@/types/waves.types";

// Enhanced wave interface with isPinned field and newDropsCount
type EnhancedWave = SidebarWave & {
  readonly isPinned: boolean;
  readonly isOfficial?: boolean;
};

/**
 * Hook for managing and fetching waves list including pinned waves
 * @returns Wave list data and loading states
 */
const useWavesList = () => {
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    address
  } = useSeizeConnectContext();
  const {
    seizeSettings,
    isAnnouncementsWave
  } = useSeizeSettings();
  const {
    pinnedIds,
    pinnedWaves: serverPinnedWaves,
    pinWave,
    unpinWave,
    isLoading: isPinnedWavesLoadingServer,
    isError: hasPinnedWavesErrorServer,
    refetch: refetchPinnedWaves
  } = usePinnedWavesServer();
  const [following] = useShowFollowingWaves();
  const announcementsWaveId = normalizeOptionalWaveId(seizeSettings.announcements_wave_id);

  // Track connected identity state - memoize to prevent re-renders
  const isConnectedIdentity = useMemo(() => {
    return !!connectedProfile?.handle && !activeProfileProxy;
  }, [connectedProfile?.handle, activeProfileProxy]);
  const viewerIdentityKey = useMemo(() => {
    if (!address) {
      return null;
    }
    const normalizedAddress = address.toLowerCase();
    if (activeProfileProxy?.id != null) {
      return `${normalizedAddress}:proxy:${activeProfileProxy.id}`;
    }
    return `${normalizedAddress}:primary`;
  }, [address, activeProfileProxy?.id]);

  // Fetch main waves list
  const {
    waves: mainWaves,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status: mainWavesStatus,
    refetch: mainWavesRefetch
  } = useWavesV2({
    overviewType: WAVE_FOLLOWING_WAVES_PARAMS.initialWavesOverviewType,
    pageSize: WAVE_FOLLOWING_WAVES_PARAMS.limit,
    following: isConnectedIdentity && following,
    directMessage: false,
    viewerIdentityKey,
    refetchInterval: SIDEBAR_WAVES_OVERVIEW_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false
  });
  const {
    waves: officialWaves,
    isFetching: isOfficialWavesFetching,
    refetch: officialWavesRefetch
  } = useOfficialWaves({
    viewerIdentityKey,
    refetchInterval: SIDEBAR_WAVES_OVERVIEW_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false
  });
  const officialWaveIds = useMemo(() => new Set(officialWaves.map(wave => wave.id)), [officialWaves]);
  const visibleOfficialWaves = useMemo(() => officialWaves.filter(wave_0 => !wave_0.isDirectMessage && !isAnnouncementsWave(wave_0.id)), [officialWaves, isAnnouncementsWave]);
  const trackedAnnouncementWave = useMemo(() => mainWaves.find(wave_3 => isAnnouncementsWave(wave_3.id)) ?? officialWaves.find(wave_2 => isAnnouncementsWave(wave_2.id)) ?? serverPinnedWaves.find(wave_1 => isAnnouncementsWave(wave_1.id)) ?? null, [mainWaves, officialWaves, serverPinnedWaves, isAnnouncementsWave]);
  const shouldFetchAnnouncementWave = Boolean(announcementsWaveId && !trackedAnnouncementWave);
  const {
    wave: fetchedAnnouncementWave,
    isLoading: rawAnnouncementQueryLoading,
    error: rawAnnouncementQueryError,
    refetch: announcementRefetch
  } = useWaveById(announcementsWaveId, {
    enabled: shouldFetchAnnouncementWave
  });
  const announcementQueryLoading = shouldFetchAnnouncementWave && rawAnnouncementQueryLoading;
  const announcementQueryError = shouldFetchAnnouncementWave ? rawAnnouncementQueryError : null;
  const fetchedAnnouncementSidebarWave = useMemo(() => fetchedAnnouncementWave ? mapApiWaveToSidebarWave(fetchedAnnouncementWave) : null, [fetchedAnnouncementWave]);
  const announcementWave = useMemo(() => {
    const resolvedWave = trackedAnnouncementWave ?? fetchedAnnouncementSidebarWave;
    if (!resolvedWave || resolvedWave.isDirectMessage) {
      return null;
    }
    return resolvedWave;
  }, [trackedAnnouncementWave, fetchedAnnouncementSidebarWave]);

  // Create a map of mainWaves by ID for easy lookup
  const mainWavesMap = useMemo(() => {
    const map = new Map<string, SidebarWave>();
    mainWaves.forEach(wave_4 => {
      if (!isAnnouncementsWave(wave_4.id) && !officialWaveIds.has(wave_4.id)) {
        map.set(wave_4.id, wave_4);
      }
    });
    return map;
  }, [mainWaves, isAnnouncementsWave, officialWaveIds]);

  // Set of wave IDs in the main list for quick checking
  const mainWaveIds = useMemo(() => {
    return new Set(mainWavesMap.keys());
  }, [mainWavesMap]);

  // Server provides full pinned waves data, so no individual fetching needed
  const missingPinnedIds = useMemo<string[]>(() => [], []);
  // Function to refetch all waves (main and pinned)
  const refetchAllWaves = useCallback(() => {
    // Refetch main waves overview
    mainWavesRefetch();
    // Refetch official waves
    officialWavesRefetch();
    // Refetch server-side pinned waves
    void refetchPinnedWaves();
    if (shouldFetchAnnouncementWave) {
      void announcementRefetch();
    }
  }, [mainWavesRefetch, officialWavesRefetch, refetchPinnedWaves, announcementRefetch, shouldFetchAnnouncementWave]);

  // Use server-provided pinned waves
  const separatelyFetchedPinnedWaves = useMemo(() => {
    // Filter out pinned waves that are already in mainWaves to avoid duplicates
    return serverPinnedWaves.filter(wave_5 => !mainWaveIds.has(wave_5.id) && !isAnnouncementsWave(wave_5.id) && !officialWaveIds.has(wave_5.id));
  }, [serverPinnedWaves, mainWaveIds, isAnnouncementsWave, officialWaveIds]);

  // Collect ALL pinned waves (both from mainWaves and server-provided)
  const allPinnedWaves = useMemo(() => {
    const result: EnhancedWave[] = [];

    // Add all server-provided pinned waves, filtering out DMs
    serverPinnedWaves.forEach(wave_6 => {
      if (!wave_6.isDirectMessage && !isAnnouncementsWave(wave_6.id) && !officialWaveIds.has(wave_6.id)) {
        result.push({
          ...wave_6,
          isPinned: true
        });
      }
    });
    return result;
  }, [serverPinnedWaves, isAnnouncementsWave, officialWaveIds]);

  // New drops counts are now managed externally

  // Combine main waves with separately fetched pinned waves using useMemo
  // Simplified order: All waves sorted by latest_drop_timestamp (most recent first)
  const combinedWaves = useMemo(() => {
    const allWavesMap = new Map<string, EnhancedWave>();
    const pinnedWavesSet = new Set(pinnedIds);
    const allWavesArray: EnhancedWave[] = [];
    [...mainWaves, ...separatelyFetchedPinnedWaves].forEach(wave_7 => {
      if (wave_7.isDirectMessage || isAnnouncementsWave(wave_7.id) || officialWaveIds.has(wave_7.id)) {
        return;
      }
      allWavesMap.set(wave_7.id, {
        ...wave_7,
        isPinned: pinnedWavesSet.has(wave_7.id)
      });
    });
    const sortedOfficialWaves = visibleOfficialWaves.map(wave_8 => ({
      ...wave_8,
      isPinned: pinnedWavesSet.has(wave_8.id),
      isOfficial: true
    })).sort((a, b) => (b.latestDropTimestamp ?? 0) - (a.latestDropTimestamp ?? 0));
    const sortedNonAnnouncementWaves = [...allWavesMap.values()].sort((a_0, b_0) => (b_0.latestDropTimestamp ?? 0) - (a_0.latestDropTimestamp ?? 0));
    if (announcementWave) {
      allWavesArray.push({
        ...announcementWave,
        isPinned: pinnedWavesSet.has(announcementWave.id)
      });
    }
    allWavesArray.push(...sortedOfficialWaves);
    allWavesArray.push(...sortedNonAnnouncementWaves);
    return allWavesArray;
  }, [mainWaves, separatelyFetchedPinnedWaves, visibleOfficialWaves, pinnedIds, announcementWave, isAnnouncementsWave, officialWaveIds]);

  // Derived data should come directly from memoized inputs
  const allWaves = combinedWaves;

  // New drops counting logic has been removed and will be managed by context

  // Use server-side loading and error states
  const isPinnedWavesLoading = isPinnedWavesLoadingServer;
  const hasPinnedWavesError = hasPinnedWavesErrorServer;

  // Memoize the fetchNextPage function to ensure it doesn't change on every render
  const fetchNextPageStable = useCallback(() => {
    return fetchNextPage();
  }, [fetchNextPage]);

  // Memoize the entire return object to prevent unnecessary re-renders in consumer components
  // Components using this hook will only re-render when the values they use actually change
  return useMemo(() => ({
    // Main data - derived from combined waves, with enhanced type
    waves: allWaves,
    // Original waves pagination and loading
    isFetching: isFetching || isOfficialWavesFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: fetchNextPageStable,
    status: mainWavesStatus,
    // Pinned waves metadata - now includes ALL pinned waves with isPinned flag
    pinnedWaves: allPinnedWaves,
    isPinnedWavesLoading,
    hasPinnedWavesError,
    trackedAnnouncementWave,
    announcementWave,
    announcementQueryLoading,
    announcementQueryError,
    announcementRefetch,
    // Pinned waves management functions
    addPinnedWave: pinWave,
    removePinnedWave: unpinWave,
    // Additional data that might be useful
    mainWaves,
    missingPinnedIds,
    mainWavesRefetch,
    // Refetch all waves including main and pinned
    refetchAllWaves
  }), [allWaves, isFetching, isOfficialWavesFetching, isFetchingNextPage, hasNextPage, fetchNextPageStable, mainWavesStatus, mainWaves, allPinnedWaves, isPinnedWavesLoading, hasPinnedWavesError, trackedAnnouncementWave, announcementWave, announcementQueryLoading, announcementQueryError, announcementRefetch, pinWave, unpinWave, missingPinnedIds, mainWavesRefetch, refetchAllWaves]);
};
export default useWavesList;